import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, User, connectAuthEmulator, setPersistence, browserLocalPersistence, browserSessionPersistence, sendPasswordResetEmail as firebaseSendPasswordResetEmail, Auth, GoogleAuthProvider, signInWithPopup, updateProfile, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, collection, getDocs, addDoc, query, where, arrayUnion, arrayRemove, deleteDoc, serverTimestamp, DocumentReference, DocumentData } from 'firebase/firestore';
import { getAnalytics, isSupported as isAnalyticsSupported } from 'firebase/analytics';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject,
  UploadTask,
  UploadTaskSnapshot
} from 'firebase/storage';
// Import the role/permission model from the shared domain module rather than from
// AuthContext — AuthContext imports from firebase.ts, so importing back from it
// created a circular dependency. The domain module has no such cycle.
import { UserRole, getDefaultPermissions } from './domain/permissions';
import { isOwnerEmail } from './config/owner';
import { isMfaChallengeError } from './utils/authErrors';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/storage';

// Firebase configuration. Web API keys are public-by-design; security comes
// from Firestore/Storage rules and App Check, not from hiding this value.
const firebaseConfig = {
  apiKey: "AIzaSyC2opn2EgaS8mhB9-CxgwJiCuHvu7DQ5Z0",
  authDomain: "superior-payroll-cw.firebaseapp.com",
  projectId: "superior-payroll-cw",
  storageBucket: "superior-payroll-cw.firebasestorage.app",
  messagingSenderId: "520825490567",
  appId: "1:520825490567:web:724a1e86927fafae457c22",
  measurementId: "G-B4V9XXM89R"
};

let app: any;
let auth: any;
let db: any;
let storage: any;
let analytics: any;
let isInitialized = false;

// Set while a registration is writing the user's profile, so the auth-state
// listener doesn't race in and seed a default Employee profile first (which
// would block the real role/company write for a self-serve Administrator).
let registrationInProgress = false;
export const isRegistrationInProgress = (): boolean => registrationInProgress;

// Function to initialize Firebase with proper sequencing
export const initializeFirebase = async () => {
  if (isInitialized) {
    return { success: true };
  }

  try {
    // Check if Firebase is already initialized
    if (!getApps().length) {
      app = initializeApp(firebaseConfig);
    } else {
      app = getApps()[0];
    }

    // Initialize Firebase services
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);

    // Analytics: skip on localhost (its dynamic-config fetch fails noisily on
    // unconfigured hosts) and only initialize when the runtime supports it.
    // Wrapped so a transient failure can't break app startup.
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // Firebase App Check — attests that requests originate from the genuine app so
    // the backend can reject scripted/abusive traffic against Firestore/Storage.
    // INERT until REACT_APP_APPCHECK_SITE_KEY is set (after a reCAPTCHA v3 provider
    // is registered in the Firebase Console). Wrapped so a failure can't break
    // startup. Roll out with per-service enforcement in MONITOR mode first.
    const appCheckSiteKey = process.env.REACT_APP_APPCHECK_SITE_KEY || '';
    if (appCheckSiteKey) {
      try {
        // On localhost, register a debug token so local dev isn't blocked by reCAPTCHA.
        if (isLocalhost && typeof window !== 'undefined') {
          (window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
        }
        initializeAppCheck(app, {
          provider: new ReCaptchaV3Provider(appCheckSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (err) {
        console.warn('App Check init failed (non-fatal):', err);
      }
    }

    if (!isLocalhost) {
      isAnalyticsSupported()
        .then((supported) => {
          if (supported) {
            try {
              analytics = getAnalytics(app);
            } catch (err) {
              console.warn('Analytics init failed (non-fatal):', err);
            }
          }
        })
        .catch((err) => console.warn('Analytics support check failed (non-fatal):', err));
    }

    // Set persistence
    await setPersistence(auth, browserLocalPersistence);

    isInitialized = true;
    return { success: true };
  } catch (error) {
    console.error('Error initializing Firebase:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown initialization error'
    };
  }
};

// Add network retry logic for Firebase operations
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;

export async function withNetworkRetry<T>(operation: () => Promise<T>): Promise<T> {
  let lastError;
  
  // Try first without any delay
  try {
    return await operation();
  } catch (error: any) {
    lastError = error;
    
    // Only retry on network errors
    if (error.code !== 'auth/network-request-failed') {
      throw error;
    }
    
    console.warn(`Network request failed (Attempt 1/${MAX_RETRIES}). Retrying in ${RETRY_DELAY_MS}ms...`);
  }

  // Try one more time after a delay
  try {
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
    return await operation();
  } catch (error: any) {
    console.error(`Failed after retry:`, error);
    throw error;
  }
}

// Function to clear any cached authentication state
export const clearAuthCache = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    console.error('Error clearing auth cache:', error.message);
    return { success: false, error: error.message };
  }
};

// Authentication functions
export const registerUser = async (email: string, password: string, userData?: any) => {
  registrationInProgress = true;
  try {
    // Use retry logic for network resilience
    const userCredential = await withNetworkRetry(() => createUserWithEmailAndPassword(auth, email, password));
    const user = userCredential.user;
    
    // If userData is provided, create user profile in Firestore
    if (userData) {
      await setDoc(doc(db, "users", user.uid), {
        ...userData,
        email,
        createdAt: new Date(),
        // Initialize friend request arrays
        friends: [],
        friendRequests: [],
        sentRequests: []
      });
    }
    
    // Approval gate: createUserWithEmailAndPassword auto-signs-in the new user.
    // Sign them out so a still-pending account can't reach the app until an
    // admin/owner approves it; they re-enter via the login gate after approval.
    await signOut(auth);
    return { success: true, user };
  } catch (error: any) {
    console.error('Registration error:', error.code, error.message);

    let errorMessage = error.message;
    if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network connection error. Please check your internet connection and try again.';
    }

    return { success: false, error: errorMessage };
  } finally {
    // Keep the guard up briefly so the auth-state listener (which fires during
    // sign-in) doesn't seed a default profile before this write settles.
    setTimeout(() => { registrationInProgress = false; }, 2500);
  }
};

/**
 * Self-serve company onboarding: creates the auth user, a company they own, and
 * an Administrator profile linked to that company. The admin is auto-approved
 * (they own the company); employees still register via registerUser as pending.
 */
export const registerCompanyAdmin = async (
  email: string,
  password: string,
  userData: any,
  companyName: string
) => {
  registrationInProgress = true;
  try {
    const userCredential = await withNetworkRetry(() => createUserWithEmailAndPassword(auth, email, password));
    const user = userCredential.user;

    // Create the company this admin owns (ownerId lets the security rules allow
    // the create even though the user's Administrator role isn't persisted yet).
    const companyRef = await addDoc(collection(db, 'companies'), {
      name: companyName,
      email,
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      ownerId: user.uid,
      createdAt: new Date(),
    });

    await setDoc(doc(db, 'users', user.uid), {
      ...userData,
      role: 'Administrator',
      email,
      companyId: companyRef.id,
      createdAt: new Date(),
      friends: [],
      friendRequests: [],
      sentRequests: [],
    });

    // Generate an employee join code so workers can link to THIS company at
    // signup (resolved via the companyJoinCodes lookup collection).
    const joinCode = await createUniqueJoinCode(companyRef.id, companyName);
    await updateDoc(companyRef, { joinCode });

    // Approval gate: sign out the auto-signed-in new admin so they can't reach
    // the app until approved (they re-enter via the login gate after approval).
    await signOut(auth);
    return { success: true, user, companyId: companyRef.id, joinCode };
  } catch (error: any) {
    console.error('Company admin registration error:', error.code, error.message);
    let errorMessage = error.message;
    if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network connection error. Please check your internet connection and try again.';
    }
    return { success: false, error: errorMessage };
  } finally {
    setTimeout(() => { registrationInProgress = false; }, 2500);
  }
};

// ---------------------------------------------------------------------------
// Company employee join codes
//
// Each company has a short code employees enter at signup to link their pending
// account to that company. Codes are the document IDs of `companyJoinCodes`,
// which map code -> { companyId, companyName } and expose nothing else, so the
// rules can let any signed-in user RESOLVE a code without exposing company data.
// ---------------------------------------------------------------------------
// Unambiguous alphabet (no 0/O/1/I/L) to make codes easy to read and share.
const JOIN_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

// Cryptographically secure join code. Math.random() is predictable (seedable,
// not CSPRNG) — a join code is a shared secret an attacker can brute-force via
// the public code->company lookup, so it must be unguessable. 8 chars over a
// 31-symbol alphabet ≈ 39.4 bits. Rejection sampling avoids modulo bias.
const generateJoinCode = (length = 8): string => {
  const n = JOIN_CODE_ALPHABET.length; // 31
  const maxUnbiased = Math.floor(256 / n) * n; // 248 — reject bytes at/above this
  const cryptoObj: Crypto | undefined =
    (typeof globalThis !== 'undefined' && (globalThis as any).crypto) || undefined;

  let code = '';
  while (code.length < length) {
    // Draw a buffer of random bytes and consume the unbiased ones.
    const buf = new Uint8Array(length - code.length + 8);
    if (cryptoObj?.getRandomValues) {
      cryptoObj.getRandomValues(buf);
    } else {
      // Last-resort fallback (non-browser/test env without WebCrypto).
      for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }
    for (let i = 0; i < buf.length && code.length < length; i++) {
      if (buf[i] < maxUnbiased) code += JOIN_CODE_ALPHABET[buf[i] % n];
    }
  }
  return code;
};

// Create a collision-free join code doc and return the code.
const createUniqueJoinCode = async (companyId: string, companyName: string): Promise<string> => {
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateJoinCode();
    const ref = doc(db, 'companyJoinCodes', code);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, { companyId, companyName, createdAt: new Date() });
      return code;
    }
  }
  const code = generateJoinCode(10);
  await setDoc(doc(db, 'companyJoinCodes', code), { companyId, companyName, createdAt: new Date() });
  return code;
};

// Resolve a join code an employee typed at signup to its company.
export const resolveCompanyJoinCode = async (
  code: string
): Promise<{ companyId: string; companyName: string } | null> => {
  try {
    const snap = await getDoc(doc(db, 'companyJoinCodes', (code || '').trim().toUpperCase()));
    if (!snap.exists()) return null;
    const d = snap.data();
    return { companyId: d.companyId, companyName: d.companyName || '' };
  } catch (error) {
    console.error('resolveCompanyJoinCode error:', error);
    return null;
  }
};

// Get a company's join code (generating + backfilling one if missing). For
// admins/owners to view and share. Caller must be able to read/update the company.
export const getOrCreateCompanyJoinCode = async (
  companyId: string
): Promise<{ success: boolean; code?: string; error?: string }> => {
  try {
    if (!companyId) return { success: false, error: 'No company associated with your account.' };
    const companyRef = doc(db, 'companies', companyId);
    const snap = await getDoc(companyRef);
    if (!snap.exists()) return { success: false, error: 'Company not found.' };
    const data = snap.data();
    if (data.joinCode) return { success: true, code: data.joinCode };
    const code = await createUniqueJoinCode(companyId, data.name || '');
    await updateDoc(companyRef, { joinCode: code });
    return { success: true, code };
  } catch (error: any) {
    console.error('getOrCreateCompanyJoinCode error:', error);
    return { success: false, error: error?.message || 'Failed to get join code.' };
  }
};

// Load a company document for the settings/profile screens.
export const getCompanyProfile = async (
  companyId: string
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    if (!companyId) return { success: false, error: 'No company associated with your account.' };
    const snap = await getDoc(doc(db, 'companies', companyId));
    if (!snap.exists()) return { success: false, error: 'Company not found.' };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (error: any) {
    console.error('getCompanyProfile error:', error);
    return { success: false, error: error?.message || 'Failed to load company.' };
  }
};

// Persist editable company-profile fields. Other fields (city/state/zip,
// ownerId, joinCode) are left untouched so the doc stays rules-valid.
export const updateCompanyProfile = async (
  companyId: string,
  fields: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    taxId?: string;
    preferences?: Record<string, any>;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!companyId) return { success: false, error: 'No company associated with your account.' };
    await updateDoc(doc(db, 'companies', companyId), { ...fields, updatedAt: new Date() });
    return { success: true };
  } catch (error: any) {
    console.error('updateCompanyProfile error:', error);
    return { success: false, error: error?.message || 'Failed to save company profile.' };
  }
};

// Translate raw Firebase/gRPC error codes into messages a user can act on.
// A bare "internal" (Firestore/Functions backend rejection) or an unmapped
// auth/* code should never reach the UI verbatim.
export const friendlyAuthError = (error: any): string => {
  const code: string = error?.code || '';
  const raw: string = error?.message || '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'network error: could not reach the authentication server.';
    case 'internal':
    case 'functions/internal':
    case 'unavailable':
    case 'functions/unavailable':
      return 'The server could not complete sign-in (backend error). Please try again; if it persists this is a configuration issue, not your password.';
    case 'permission-denied':
    case 'functions/permission-denied':
      return 'Your account is not permitted to sign in here.';
    default:
      // Don't leak a bare code like "internal" — fall back to a generic line
      // but keep anything that already reads like a sentence.
      return raw && /\s/.test(raw) ? raw : 'Sign-in failed. Please try again.';
  }
};

export const loginUser = async (email: string, password: string, persistLogin: boolean = false): Promise<{ success: boolean; user?: User; error?: string; pendingApproval?: boolean; redirectUrl?: string; mfaError?: any }> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Get user profile data
    const userDocRef = doc(db, 'users', userCredential.user.uid);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      
      // Check account status and approval
      if (userData.status === "pending" || userData.accountApproved === false) {
        await signOut(auth);
        return { 
          success: false, 
          pendingApproval: true,
          error: "Your account is pending administrator approval. You'll be able to sign in once an administrator approves it."
        };
      } else if (userData.status === "rejected") {
        await signOut(auth);
        return { 
          success: false, 
          error: "Your account registration has been declined by an administrator." 
        };
      }

      // Record last login (best-effort; never blocks sign-in). A self-update of this
      // non-privileged field is permitted by the users security rule. Surfaced in the
      // Owner Console "Last Login" column.
      try {
        await updateDoc(userDocRef, { lastLoginAt: serverTimestamp() });
      } catch (e) {
        console.warn('Could not record lastLoginAt:', e);
      }

      // Determine redirect URL based on role
      const redirectUrl = userData.role === 'Employee' ? '/employee-dashboard' : '/dashboard';

      // If "stay signed in" is enabled, save info to localStorage
      if (persistLogin) {
        localStorage.setItem('persistentLogin', 'true');
        const userData = {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName,
          photoURL: userCredential.user.photoURL
        };
        localStorage.setItem('authUser', JSON.stringify(userData));
      } else {
        localStorage.removeItem('persistentLogin');
        localStorage.removeItem('authUser');
      }
      
      return { success: true, user: userCredential.user, redirectUrl };
    }
    
    return { success: true, user: userCredential.user, redirectUrl: '/dashboard' };
  } catch (error: any) {
    // Log the underlying code so a bare "internal" is diagnosable from the console.
    console.error('Login error:', error?.code || '(no code)', error);
    // A user with MFA enrolled hits this path: signInWithEmailAndPassword throws
    // `auth/multi-factor-auth-required` BEFORE returning a credential. We must
    // surface the raw error so the caller can build a MultiFactorResolver and
    // prompt for the second factor — otherwise MFA users can never sign in.
    if (isMfaChallengeError(error)) {
      return { success: false, mfaError: error };
    }
    return {
      success: false,
      error: friendlyAuthError(error),
    };
  }
};

// Change the signed-in user's password. Firebase requires a recent login to
// change credentials, so we re-authenticate with the current password first.
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const user = auth.currentUser;
    if (!user || !user.email) {
      return { success: false, error: 'You must be signed in to change your password.' };
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error: any) {
    console.error('Change password error:', error?.code || '', error);
    const code = error?.code || '';
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      return { success: false, error: 'Your current password is incorrect.' };
    }
    if (code === 'auth/weak-password') {
      return { success: false, error: 'New password is too weak (use at least 6 characters).' };
    }
    if (code === 'auth/requires-recent-login') {
      return { success: false, error: 'Please sign out and back in, then try again.' };
    }
    return { success: false, error: error?.message || 'Failed to change password.' };
  }
};

export const logoutUser = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const auth = getAuth();
    await signOut(auth);
    
    // Clear any local storage data related to authentication
    localStorage.removeItem('authUser');
    localStorage.removeItem('persistentLogin');
    localStorage.removeItem('userProfile');
    
    return { success: true };
  } catch (error: any) {
    console.error("Logout error:", error);
    return { success: false, error: error.message };
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

// Define return type interfaces for better type checking
interface ProfileSuccessResult {
  success: true;
  data: any;
}

interface ProfileErrorResult {
  success: false;
  error: string;
  notFound?: boolean;
}

// Union type for the possible return values
type ProfileResult = ProfileSuccessResult | ProfileErrorResult;

export const getUserProfile = async (userId: string): Promise<ProfileResult> => {
  try {
    // Use retry logic for network resilience
    return await withNetworkRetry(async () => {
      const docRef = doc(db, "users", userId);

      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Convert Firestore timestamps to Dates
        const processedData = {
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt
        };
        
        return { success: true, data: processedData } as ProfileSuccessResult;
      } else {
        return {
          success: false,
          notFound: true,
          error: "User profile not found in database. This could be due to incomplete registration or a data issue."
        } as ProfileErrorResult;
      }
    });
  } catch (error: any) {
    console.error(`Error getting user profile for ${userId}:`, error);
    
    let errorMessage = error.message;
    if (error.code === 'permission-denied') {
      errorMessage = "Permission denied accessing user profile. Please check Firestore security rules.";
    } else if (error.code === 'unavailable') {
      errorMessage = "Firestore service is currently unavailable. Please try again later.";
    }
    
    return { success: false, error: errorMessage } as ProfileErrorResult;
  }
};

export const updateUserProfile = async (userId: string, data: any) => {
  try {
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      ...data,
      updatedAt: new Date()
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Send a password reset email to the user
export const sendPasswordResetEmail = async (email: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Use retry logic for password reset
    await withNetworkRetry(() => firebaseSendPasswordResetEmail(auth, email, {
      // URL to redirect to after password reset
      url: `${window.location.origin}/login`,
      handleCodeInApp: false,
    }));
    return { success: true };
  } catch (error: any) {
    console.error('Error sending password reset email:', error);
    
    let errorMessage = error.message;
    if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network connection error. Please check your internet connection and try again.';
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

// Upload a profile photo with CORS workaround
export const uploadProfilePhoto = async (
  file: File | string, 
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error("❌ Authentication error: No current user");
      throw new Error('User not authenticated');
    }

    // Create a storage reference
    try {
      const storageRef = ref(storage, `profile-photos/${user.uid}/profile.jpg`);

      // CORS Workaround:
      // 1. First convert everything to a blob
      let fileBlob: Blob;

      // If it's a string (data URL), convert to blob
      if (typeof file === 'string') {
        if (file.startsWith('data:')) {
          try {
            const response = await fetch(file);
            fileBlob = await response.blob();
          } catch (error) {
            console.error('❌ Error converting data URL to blob:', error);
            throw new Error('Failed to process the image data');
          }
        } else {
          // It's a file path - we can't handle this directly
          console.error("❌ File path uploads not supported");
          throw new Error('File path uploads not supported');
        }
      } else {
        // It's already a File object (which is a Blob)
        fileBlob = file;
      }

      // 2. Create metadata with extra information to help debugging
      const metadata = {
        contentType: fileBlob.type,
        customMetadata: {
          'uploaded-by': user.uid,
          'upload-date': new Date().toISOString(),
          'cors-bypass': 'true',
          'upload-client': navigator.userAgent
        }
      };

      // 3. Create the upload task
      try {
        const uploadTask = uploadBytesResumable(storageRef, fileBlob, metadata);

        // 4. Set up progress monitoring
        if (onProgress) {
          uploadTask.on('state_changed',
            (snapshot: UploadTaskSnapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

              onProgress(progress);
            },
            (error) => {
              console.error('❌ Upload error:', error.code, error.message);
              console.error('Error details:', error);
              throw error;
            },
            () => {
            }
          );
        }

        // 5. Wait for the upload to complete
        try {
          await uploadTask;

          // 6. Get the download URL
          try {
            const downloadURL = await getDownloadURL(storageRef);

            // 7. Update the user's profile
            try {
              await updateProfile(user, {
                photoURL: downloadURL
              });
            } catch (error) {
              console.error('⚠️ Error updating user profile:', error);
              // Don't fail the whole operation if just the profile update fails
            }
            
            return downloadURL;
          } catch (urlError) {
            console.error('❌ Error getting download URL:', urlError);
            throw urlError;
          }
        } catch (awaitError) {
          console.error('❌ Error waiting for upload to complete:', awaitError);
          throw awaitError;
        }
      } catch (uploadTaskError) {
        console.error('❌ Error creating upload task:', uploadTaskError);
        throw uploadTaskError;
      }
    } catch (storageRefError) {
      console.error('❌ Error creating storage reference:', storageRefError);
      throw storageRefError;
    }
  } catch (error: any) {
    console.error('❌❌❌ TOP LEVEL ERROR in uploadProfilePhoto:', error);
    console.error('Error details:', error.code, error.message, error.stack);
    throw error;
  }
};

// Fix the deleteProfilePhoto function to properly remove photo
export const deleteProfilePhoto = async (userId: string): Promise<OperationResult> => {
  try {
    const userRef = doc(db, 'users', userId);
    const user = auth.currentUser;
    
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }
    
    // Check if there's a photo to delete in storage
    if (user.photoURL && user.photoURL.includes('firebasestorage')) {
      try {
        // Get storage reference from the URL
        const photoRef = ref(storage, user.photoURL);
        // Delete from storage
        await deleteObject(photoRef);
      } catch (storageError) {
        // Continue even if storage deletion fails
        console.warn('Error deleting from storage:', storageError);
      }
    }
    
    // Update the user profile in Firestore
    await updateDoc(userRef, {
      photoURL: null,
      updatedAt: new Date()
    });
    
    // Also update the auth profile
    await updateProfile(user, {
      photoURL: null
    });
    
    // Clear any local storage cache
    try {
      localStorage.removeItem('currentProfilePhoto');
    } catch (e) {
      // Ignore local storage errors
    }

    // Force a refresh of the auth token
    await user.reload();
    
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting profile photo:', error);
    return { success: false, error: error.message };
  }
};

// Extended function to update the user profile with more customizable fields
export const updateUserProfileDetail = async (userId: string, data: any) => {
  try {
    const userRef = doc(db, "users", userId);
    
    // Remove null values (convert them to undefined for Firestore)
    const cleanData = Object.entries(data).reduce((acc: any, [key, value]) => {
      // If value is an object (like socialLinks or preferences), process it recursively
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const cleanNestedData = Object.entries(value as object).reduce((nestedAcc: any, [nestedKey, nestedValue]) => {
          if (nestedValue !== null) {
            nestedAcc[nestedKey] = nestedValue;
          }
          return nestedAcc;
        }, {});
        
        acc[key] = cleanNestedData;
      } else if (value !== null) {
        acc[key] = value;
      }
      
      return acc;
    }, {});
    
    await updateDoc(userRef, {
      ...cleanData,
      updatedAt: new Date()
    });
    
    return { success: true };
  } catch (error: any) {
    console.error("Error updating user profile details:", error);
    return { success: false, error: error.message };
  }
};

// Function to create a default user profile if one doesn't exist
export const createDefaultUserProfile = async (userId: string, email: string): Promise<ProfileResult> => {
  try {
    // Check if profile already exists
    const existingProfile = await getUserProfile(userId);
    if (existingProfile.success) {
      return existingProfile;
    }
    
    // Create a default profile
    const defaultProfile = {
      uid: userId,
      email: email,
      firstName: email.split('@')[0], // Use email username as first name initially
      lastName: '',
      role: 'Employee' as UserRole, // Default role
      createdAt: new Date(),
      updatedAt: new Date(),
      permissions: getDefaultPermissions('Employee'),
      friends: [], // Initialize friends array
      friendRequests: [], // Initialize friend requests array
      sentRequests: [] // Initialize sent requests array
    };
    
    // Save to Firestore
    const userRef = doc(db, "users", userId);
    await setDoc(userRef, defaultProfile);

    return {
      success: true,
      data: defaultProfile
    } as ProfileSuccessResult;
  } catch (error: any) {
    console.error(`Error creating default user profile for ${userId}:`, error);
    return { 
      success: false, 
      error: `Failed to create user profile: ${error.message}` 
    } as ProfileErrorResult;
  }
};

// Fallback upload method that tries multiple approaches
export const uploadProfilePhotoFallback = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Try to upload using the most reliable method
  try {
    if (onProgress) {
      onProgress(10); // Show initial progress
    }

    // Create a storage reference with a unique path
    const timestamp = new Date().getTime();
    const storageRefPath = `profile-photos/${user.uid}/${timestamp}-profile.jpg`;
    const storageRef = ref(storage, storageRefPath);

    if (onProgress) {
      onProgress(20); // Reference created
    }
    
    // Simple direct upload without data URL conversion
    // This approach avoids the problematic fetch() call
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        'uploaded-by': user.uid,
        'upload-timestamp': timestamp.toString(),
        'original-filename': file.name,
        'upload-method': 'direct-blob'
      }
    });

    if (onProgress) {
      onProgress(40); // Upload started
    }

    // Monitor upload state
    await new Promise<void>((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            // Scale progress from 40-90%
            onProgress(40 + (progress * 0.5));
          }
        },
        (error: Error) => {
          console.error('Upload failed:', error);
          reject(error);
        },
        async () => {
          resolve();
        }
      );
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);

    if (onProgress) {
      onProgress(95); // Almost done
    }
    
    // Update user profile
    await updateProfile(user, {
      photoURL: downloadURL
    });
    
    if (onProgress) {
      onProgress(100); // Complete
    }
    
    return downloadURL;
  } catch (error) {
    console.error('Fallback upload failed:', error);
    throw error;
  }
};

// Simplified maximum compatibility upload method
export const uploadProfilePhotoCompat = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  // Show initial progress
  if (onProgress) onProgress(5);
  
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  // Show authentication confirmed
  if (onProgress) onProgress(10);
  
  // Create a timestamp for unique filename
  const timestamp = new Date().getTime();
  const fileName = `${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
  const storageRefPath = `profile-photos/${user.uid}/${fileName}`;
  
  try {
    // First try to use the compat API (v8 style)
    const storageRef = firebase.storage().ref(storageRefPath);

    if (onProgress) onProgress(20);
    
    // Create upload task using the v8 put method
    const uploadTask = storageRef.put(file, {
      contentType: file.type,
      customMetadata: {
        'uploaded-by': user.uid,
        'upload-method': 'compat-v8-direct'
      }
    });

    if (onProgress) onProgress(30);

    // Return a promise that resolves when upload is complete
    return new Promise((resolve, reject) => {
      // Track upload progress
      uploadTask.on(
        'state_changed',
        (snapshot: any) => {
          // Calculate progress percentage
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

          // Scale progress from 30-90%
          if (onProgress) {
            onProgress(30 + (progress * 0.6));
          }
        },
        (error: Error) => {
          console.error("Upload failed:", error);
          reject(error);
        },
        async () => {
          try {
            // Get download URL
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();

            if (onProgress) onProgress(95);
            
            // Update user profile
            await updateProfile(user, {
              photoURL: downloadURL
            });
            
            if (onProgress) onProgress(100);
            
            resolve(downloadURL);
          } catch (urlError) {
            console.error("Error getting download URL:", urlError);
            reject(urlError);
          }
        }
      );
    });
  } catch (error) {
    console.error("Initial upload attempt failed:", error);
    
    // If the compat API fails, try the modular API as fallback
    try {
      // Create reference with modular API
      const storageRef = ref(storage, storageRefPath);
      
      // Upload with modular API
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type
      });
      
      // Return a promise that resolves when upload is complete
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(30 + (progress * 0.6));
            }
          },
          (error: Error) => {
            console.error("Fallback upload failed:", error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              
              await updateProfile(user, {
                photoURL: downloadURL
              });
              
              if (onProgress) onProgress(100);
              
              resolve(downloadURL);
            } catch (urlError) {
              reject(urlError);
            }
          }
        );
      });
    } catch (fallbackError) {
      console.error("Both upload methods failed:", fallbackError);
      throw fallbackError;
    }
  }
};

// `restoreAuthState` was removed — it returned a fake User from localStorage
// with `getIdToken: async () => null`, which let routes trust an unauthenticated
// caller for a window after page load. Auth state is sourced exclusively from
// Firebase's `onAuthStateChanged`.

// Add the OperationResult type definition
export interface OperationResult {
  success: boolean;
  error?: string;
  data?: any;
}

export { app, auth, db, storage, analytics };

// Get all users with pending status
export const getPendingUsers = async (): Promise<{ success: boolean; data?: any[]; error?: string }> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'You must be signed in.' };
    }

    const usersRef = collection(db, "users");

    // Scope the query to satisfy the Firestore rules:
    //  - Platform owner may list every pending user (rule is data-independent).
    //  - Company staff may only list pending users IN THEIR OWN company, so we
    //    must filter by companyId or Firestore rejects the whole query.
    //  - Anyone without a company (e.g. the seed admin) gets an empty list, not
    //    a permission error.
    let pendingUsersQuery;
    if (isOwnerEmail(user.email)) {
      pendingUsersQuery = query(usersRef, where("status", "==", "pending"));
    } else {
      const meSnap = await getDoc(doc(db, "users", user.uid));
      const companyId = meSnap.exists() ? (meSnap.data().companyId || '') : '';
      if (!companyId) {
        return { success: true, data: [] };
      }
      pendingUsersQuery = query(
        usersRef,
        where("companyId", "==", companyId),
        where("status", "==", "pending")
      );
    }

    const querySnapshot = await getDocs(pendingUsersQuery);
    
    const pendingUsers: any[] = [];
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      pendingUsers.push({
        id: doc.id,
        ...userData,
        createdAt: userData.createdAt?.toDate() || userData.createdAt,
        updatedAt: userData.updatedAt?.toDate() || userData.updatedAt,
      });
    });
    
    return { success: true, data: pendingUsers };
  } catch (error: any) {
    console.error("Error getting pending users:", error);
    return { success: false, error: error.message };
  }
};

// Approve a user account
export const approveUserAccount = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, "users", userId);
    
    await updateDoc(userRef, {
      status: "active",
      accountApproved: true,
      updatedAt: new Date(),
      approvedAt: new Date()
    });
    
    // Here you would typically also send an email notification to the user
    // that their account has been approved
    
    return { success: true };
  } catch (error: any) {
    console.error("Error approving user account:", error);
    return { success: false, error: error.message };
  }
};

// Reject a user account 
export const rejectUserAccount = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const userRef = doc(db, "users", userId);
    
    await updateDoc(userRef, {
      status: "rejected",
      accountApproved: false,
      updatedAt: new Date(),
      rejectedAt: new Date()
    });
    
    // Here you would typically also send an email notification to the user
    // that their account has been rejected
    
    return { success: true };
  } catch (error: any) {
    console.error("Error rejecting user account:", error);
    return { success: false, error: error.message };
  }
};

// Function to initialize Firebase with persistence
export const initializeFirebaseWithPersistence = async () => {
  try {
    // Initialize Firebase first
    const app = initializeApp(firebaseConfig);

    // Get auth instance
    const auth = getAuth(app);

    // Set persistence
    await setPersistence(auth, browserLocalPersistence);

    // Initialize other services
    const db = getFirestore(app);

    const storage = getStorage(app);

    return { success: true };
  } catch (error) {
    console.error('Error during Firebase initialization:', error);
    // Log additional error details if available
    if (error instanceof Error) {
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    return { success: false, error };
  }
}; 